import { useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { cn } from "../../utils/cn";
import { getImageSrc } from "../../utils/lib/utils";
import Modal from "../../components/common/Modal";
import Permission from "../../components/auth/Permission";
import { organisationServices } from "../../service/companySettingService";
import {
  LayoutDashboard,
  Tag,
  Layers,
  Package,
  Warehouse,
  Calculator,
  ShoppingCart,
  RotateCcw,
  Truck,
  RefreshCw,
  Users,
  Building2,
  UserCog,
  Landmark,
  CreditCard,
  AlertTriangle,
  BarChart2,
  UserCircle,
  Settings,
  LogOut,
  Clock3,
} from "lucide-react";

type SidebarItemType = {
  title: string;
  icon: React.ReactNode;
  path: string;
  roles?: string[];
};

const menuItems: SidebarItemType[] = [
  {
    title: "Dashboard",
    icon: <LayoutDashboard size={17} />,
    path: "/",
    roles: ["ADMIN"],
  },
  {
    title: "Category",
    icon: <Tag size={17} />,
    path: "/category",
  },
  // {
  //   title: "Brands",
  //   icon: <Layers size={17} />,
  //   path: "/brands",
  // },
  {
    title: "Product",
    icon: <Package size={17} />,
    path: "/product",
  },
  {
    title: "Inventory",
    icon: <Warehouse size={17} />,
    path: "/inventory",
  },
  {
    title: "Accounting",
    icon: <Calculator size={17} />,
    path: "/accounting-list",
    roles: ["USER", "MANAGER"],
  },
  {
    title: "EOD Balance",
    icon: <Calculator size={17} />,
    path: "/accounting/eod",
    roles: ["ADMIN"],
  },
  {
    title: "Sales",
    icon: <ShoppingCart size={17} />,
    path: "/sales",
    roles: ["USER", "MANAGER"],
  },
  {
    title: "Sales Return",
    icon: <RotateCcw size={17} />,
    path: "/sales-return",
    roles: ["USER", "MANAGER"],
  },
  {
    title: "Purchase",
    icon: <Truck size={17} />,
    path: "/purchase",
    roles: ["MANAGER"],
  },
  {
    title: "Purchase Return",
    icon: <RefreshCw size={17} />,
    path: "/purchase-return",
    roles: ["MANAGER"],
  },
  {
    title: "Customer",
    icon: <Users size={17} />,
    path: "/customer",
  },
  {
    title: "Vendor Management",
    icon: <Building2 size={17} />,
    path: "/vendor-management",
  },
  {
    title: "Staff Management",
    icon: <UserCog size={17} />,
    path: "/staff-management",
    roles: ["ADMIN"],
  },
  {
    title: "Session Manager",
    icon: <Clock3 size={17} />,
    path: "/session-manager",
    roles: ["ADMIN"],
  },
  {
    title: "Banking",
    icon: <Landmark size={17} />,
    path: "/banking",
    roles: ["MANAGER"],
  },
  {
    title: "POS Settlement",
    icon: <CreditCard size={17} />,
    path: "/pos-settlement",
    roles: ["MANAGER"],
  },
  {
    title: "Warranty ",
    icon: <AlertTriangle size={17} />,
    path: "/warranty",
  },
  {
    title: "Dead Stock",
    icon: <AlertTriangle size={17} />,
    path: "/dead-stock-management",
    roles: ["MANAGER", "ADMIN"],
  },
];

const reportItems: SidebarItemType[] = [
  {
    title: "Reports",
    icon: <BarChart2 size={17} />,
    path: "/reports",
    roles: ["ADMIN"],
  },
  {
    title: "Users",
    icon: <UserCircle size={17} />,
    path: "/users",
    roles: ["ADMIN"],
  },
];

function Sidebar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { data: organisationData } = organisationServices.useGetOrganisation();
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("userName");
    localStorage.removeItem("username");
    localStorage.removeItem("branchId");
    localStorage.removeItem("counterId");
    localStorage.removeItem("orgId");
    localStorage.removeItem("counterSessionStatus");
    localStorage.removeItem("isCounterOpen");
    navigate("/login");
  };

  const isActive = (item: SidebarItemType) => {
    if (item.path === "/") return pathname === "/";
    // Use exact match or sub-route match with a trailing slash guard
    // so "/purchase" does NOT match "/purchase-return"
    return pathname === item.path || pathname.startsWith(item.path + "/");
  };

  const SidebarItem = ({ item }: { item: SidebarItemType }) => {
    const active = isActive(item);
    const tourId = item.path.replace("/", "");
    return (
      <button
        type="button"
        data-tour={tourId}
        onClick={() => navigate(item.path)}
        className={cn(
          "w-full flex items-center gap-3 px-3 py-2.5 rounded-[12px] text-left transition-all duration-150 group",
          active
            ? "bg-[#1A5EF9] text-white shadow-[0_4px_12px_rgba(26,94,249,0.25)]"
            : "text-[#52627C] hover:bg-[#F0F4FF] hover:text-[#1A5EF9]",
        )}
      >
        <span
          className={cn(
            "flex-shrink-0 transition-colors",
            active ? "text-white" : "text-[#8A9BB5] group-hover:text-[#1A5EF9]",
          )}
        >
          {item.icon}
        </span>
        <span className="text-[13px] font-medium truncate">{item.title}</span>
        {active && (
          <span className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 flex-shrink-0" />
        )}
      </button>
    );
  };

  const SectionLabel = ({ label }: { label: string }) => (
    <p className="px-3 pt-4 pb-1.5 text-[10px] font-bold uppercase tracking-widest text-[#B0BCCE]">
      {label}
    </p>
  );

  return (
    <div className="bg-white w-[240px] border-r border-[#EEF2FA] flex flex-col h-screen overflow-hidden">
      {/* Logo / Brand */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-[#EEF2FA] flex-shrink-0">
        <div
          className="w-10 h-10 rounded-[12px] bg-[#F0F4FF] border border-[#D6E4FF] flex items-center justify-center flex-shrink-0 cursor-pointer overflow-hidden"
          onClick={() => navigate("/")}
        >
          <img
            src={getImageSrc("/images/codenzic.jpeg")}
            alt="logo"
            className="w-10 h-10 object-cover"
          />
        </div>
        <div className="min-w-0">
          <p className="text-[13px] font-bold text-[#1A2B4A] truncate leading-tight">
            {organisationData?.name || "Hakamani ERP"}
          </p>
          <p className="text-[11px] text-[#8A9BB5] leading-tight mt-0.5">
            Sales Workspace
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5 scrollbar-thin scrollbar-thumb-[#E8EEF9] scrollbar-track-transparent">
        <SectionLabel label="Menu" />
        {menuItems.map((item, index) => {
          if (item.roles) {
            return (
              <Permission key={index} roles={item.roles}>
                <SidebarItem item={item} />
              </Permission>
            );
          }
          return <SidebarItem key={index} item={item} />;
        })}
        <Permission roles={["ADMIN"]}>
          <SectionLabel label="Reports" />
        </Permission>
        {reportItems.map((item, index) => {
          if (item.roles) {
            return (
              <Permission key={index} roles={item.roles}>
                <SidebarItem item={item} />
              </Permission>
            );
          }
          return <SidebarItem key={index} item={item} />;
        })}
      </div>

      {/* Account footer */}
      <div className="border-t border-[#EEF2FA] px-3 py-3 flex-shrink-0 space-y-0.5">
        <Permission roles={["ADMIN"]}>
          <button
            type="button"
            onClick={() => navigate("/company-settings")}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-[12px] text-left transition-all duration-150 group",
              pathname === "/company-settings"
                ? "bg-[#1A5EF9] text-white shadow-[0_4px_12px_rgba(26,94,249,0.25)]"
                : "text-[#52627C] hover:bg-[#F0F4FF] hover:text-[#1A5EF9]",
            )}
          >
            <Settings
              size={17}
              className={cn(
                "flex-shrink-0 transition-colors",
                pathname === "/company-settings"
                  ? "text-white"
                  : "text-[#8A9BB5] group-hover:text-[#1A5EF9]",
              )}
            />
            <span className="text-[13px] font-medium">Settings</span>
          </button>
        </Permission>

        <button
          type="button"
          onClick={() => setShowLogoutModal(true)}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-[12px] text-left transition-all duration-150 group text-[#52627C] hover:bg-red-50 hover:text-red-600"
        >
          <LogOut
            size={17}
            className="flex-shrink-0 text-[#8A9BB5] group-hover:text-red-500 transition-colors"
          />
          <span className="text-[13px] font-medium">Logout</span>
        </button>
      </div>

      {/* Logout Confirmation Modal */}
      <Modal open={showLogoutModal} setOpen={setShowLogoutModal}>
        <div className="p-6 flex flex-col items-center text-center justify-center  ">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <LogOut size={22} className="text-red-500" />
          </div>
          <h3 className="text-base font-semibold text-[#1A2B4A] mb-1">
            Sign out?
          </h3>
          <p className="text-sm text-[#52627C] mb-6">
            You&apos;ll be returned to the login screen.
          </p>
          <div className="flex gap-3 w-full">
            <button
              className="flex-1 h-10 rounded-[10px] border border-[#E5ECF8] text-[#52627C] text-sm font-medium hover:bg-[#F5F8FD] transition-colors"
              onClick={() => setShowLogoutModal(false)}
            >
              Cancel
            </button>
            <button
              className="flex-1 h-10 rounded-[10px] bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
              onClick={handleLogout}
            >
              Sign out
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Sidebar;
